import { useCallback, useEffect, useRef, useState } from 'react';

import {
  loadCachedPosts,
  saveCachedPosts,
} from '@/src/features/posts/feed-cache';
import {
  FEED_RANKING_VERSION,
  getFeedPosts,
  mergeRankedPostsWithLivePosts,
  PUBLIC_POST_LIMIT,
  refreshMyRankedFeedsWithCooldown,
  subscribeToRankedPosts,
  subscribeToFollowingPosts,
  type FeedMode,
  type PublicPost,
} from '@/src/features/posts/post-service';

const LATEST_FEED_REFRESH_INTERVAL_MS = 60_000;

type FollowingPostsState = {
  error: Error | null;
  followingCount: number;
  loading: boolean;
  posts: PublicPost[];
};

export function useFollowingPosts(
  uid: string | undefined,
  limitCount = PUBLIC_POST_LIMIT,
  mode: FeedMode = 'ranked',
  enabled = true,
) {
  const [effectiveLimit, setEffectiveLimit] = useState(limitCount);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const postsRef = useRef<PublicPost[]>([]);
  const rankedPostsRef = useRef<PublicPost[]>([]);
  const livePostsRef = useRef<PublicPost[]>([]);
  const rankedReadyRef = useRef(false);
  const liveReadyRef = useRef(false);
  const followingCountRef = useRef(0);
  const uidRef = useRef<string | undefined>(undefined);
  const refreshRequestedRef = useRef(false);
  const [state, setState] = useState<FollowingPostsState>({
    error: null,
    followingCount: 0,
    loading: Boolean(uid),
    posts: [],
  });
  const cacheKey =
    mode === 'ranked' && uid
      ? `ranked:frienzzone:${uid}:v${FEED_RANKING_VERSION}`
      : mode === 'latest' && uid
        ? `latest:frienzzone:${uid}:v${FEED_RANKING_VERSION}`
        : `following:${uid ?? 'signed-out'}`;

  useEffect(() => {
    setEffectiveLimit(limitCount);
  }, [limitCount]);

  useEffect(() => {
    const subscriptionKey = `${uid ?? ''}:${mode}`;

    if (uidRef.current !== subscriptionKey) {
      uidRef.current = subscriptionKey;
      postsRef.current = [];
      rankedPostsRef.current = [];
      livePostsRef.current = [];
      rankedReadyRef.current = false;
      liveReadyRef.current = false;
    }

    if (!uid) {
      postsRef.current = [];
      followingCountRef.current = 0;
      setLoadingMore(false);
      setRefreshing(false);
      setState({
        error: null,
        followingCount: 0,
        loading: false,
        posts: [],
      });
      return undefined;
    }

    if (!enabled) {
      setLoadingMore(false);
      setRefreshing(false);
      setState((currentState) => ({
        ...currentState,
        error: null,
        loading: false,
      }));
      return undefined;
    }

    setState((currentState) => ({
      ...currentState,
      error: null,
      loading: postsRef.current.length === 0,
      posts:
        postsRef.current.length === 0 ? currentState.posts : postsRef.current,
    }));

    if (postsRef.current.length === 0) {
      void loadCachedPosts(cacheKey).then((cachedPosts) => {
        if (cachedPosts.length > 0) {
          postsRef.current = cachedPosts;
          setState((currentState) => ({
            ...currentState,
            loading: false,
            posts: cachedPosts,
          }));
        }
      });
    }

    const useRankedFeed = mode === 'ranked';
    const useServerLatestFeed = mode === 'latest';

    const publishRankedPosts = (followingCount: number) => {
      if (!rankedReadyRef.current || !liveReadyRef.current) {
        return;
      }

      const mergedPosts = mergeRankedPostsWithLivePosts({
        limitCount: effectiveLimit,
        livePosts: livePostsRef.current,
        rankedPosts: rankedPostsRef.current,
      });

      postsRef.current = mergedPosts;
      setState({
        error: null,
        followingCount,
        loading: false,
        posts: mergedPosts,
      });
      setLoadingMore(false);
      setRefreshing(false);
      void saveCachedPosts(cacheKey, mergedPosts);
    };

    if (useRankedFeed) {
      void refreshMyRankedFeedsWithCooldown({
        feedKind: 'frienzzone',
        uid,
      }).catch(() => undefined);

      let latestFollowingCount = followingCountRef.current;
      const rankedUnsubscribe = subscribeToRankedPosts({
        feedKind: 'frienzzone',
        limitCount: effectiveLimit,
        uid,
        onError: (error) => {
          setState((currentState) => ({
            ...currentState,
            error,
            loading: false,
          }));
          setLoadingMore(false);
          setRefreshing(false);
        },
        onNext: (posts) => {
          rankedPostsRef.current = posts;
          rankedReadyRef.current = true;
          publishRankedPosts(latestFollowingCount);

          if (posts.length === 0 && !refreshRequestedRef.current) {
            refreshRequestedRef.current = true;
            void refreshMyRankedFeedsWithCooldown({
              feedKind: 'frienzzone',
              force: true,
              uid,
            }).catch(() => undefined);
          }
        },
      });
      livePostsRef.current = [];
      liveReadyRef.current = true;
      void getFeedPosts({
        feedKind: 'frienzzone',
        limitCount: 1,
      })
        .then((result) => {
          followingCountRef.current = result.followingCount;
          latestFollowingCount = result.followingCount;
          publishRankedPosts(result.followingCount);
        })
        .catch(() => undefined);

      return () => {
        rankedUnsubscribe();
      };
    }

    if (useServerLatestFeed) {
      let cancelled = false;
      let fallbackUnsubscribe: (() => void) | undefined;
      let latestLoadInFlight = false;

      const loadServerLatestPosts = () => {
        if (latestLoadInFlight) {
          return;
        }

        latestLoadInFlight = true;
        void getFeedPosts({
          feedKind: 'frienzzone',
          limitCount: effectiveLimit,
        })
          .then((result) => {
            if (cancelled) {
              return;
            }

            followingCountRef.current = result.followingCount;
            postsRef.current = result.posts;
            setState({
              error: null,
              followingCount: result.followingCount,
              loading: false,
              posts: result.posts,
            });
            setLoadingMore(false);
            setRefreshing(false);
            void saveCachedPosts(cacheKey, result.posts);
          })
          .catch((error) => {
            if (cancelled) {
              return;
            }

            if (
              typeof __DEV__ !== 'undefined' &&
              __DEV__ &&
              !fallbackUnsubscribe
            ) {
              fallbackUnsubscribe = subscribeToFollowingPosts({
                limitCount: effectiveLimit,
                uid,
                onError: (fallbackError) => {
                  setState((currentState) => ({
                    ...currentState,
                    error: fallbackError,
                    loading: false,
                  }));
                  setLoadingMore(false);
                  setRefreshing(false);
                },
                onNext: (result) => {
                  followingCountRef.current = result.followingCount;
                  postsRef.current = result.posts;
                  setState({
                    error: null,
                    followingCount: result.followingCount,
                    loading: false,
                    posts: result.posts,
                  });
                  setLoadingMore(false);
                  setRefreshing(false);
                  void saveCachedPosts(cacheKey, result.posts);
                },
              });
              return;
            }

            setState((currentState) => ({
              ...currentState,
              error:
                error instanceof Error
                  ? error
                  : new Error('Unable to load posts.'),
              loading: false,
            }));
            setLoadingMore(false);
            setRefreshing(false);
          })
          .finally(() => {
            latestLoadInFlight = false;
          });
      };

      loadServerLatestPosts();
      const latestRefreshInterval = setInterval(
        loadServerLatestPosts,
        LATEST_FEED_REFRESH_INTERVAL_MS,
      );

      return () => {
        cancelled = true;
        clearInterval(latestRefreshInterval);
        fallbackUnsubscribe?.();
      };
    }

    return subscribeToFollowingPosts({
      limitCount: effectiveLimit,
      uid,
      onError: (error) => {
        setState((currentState) => ({
          ...currentState,
          error,
          loading: false,
        }));
        setLoadingMore(false);
        setRefreshing(false);
      },
      onNext: ({ followingCount, posts }) => {
        followingCountRef.current = followingCount;
        postsRef.current = posts;
        setState({ error: null, followingCount, loading: false, posts });
        setLoadingMore(false);
        setRefreshing(false);
        void saveCachedPosts(cacheKey, posts);
      },
    });
  }, [cacheKey, effectiveLimit, enabled, mode, refreshNonce, uid]);

  const hasMore = state.posts.length >= effectiveLimit;

  const loadMore = useCallback(() => {
    if (state.loading || loadingMore || refreshing || !hasMore) {
      return;
    }

    setLoadingMore(true);
    setEffectiveLimit((currentLimit) => currentLimit + PUBLIC_POST_LIMIT);
  }, [hasMore, loadingMore, refreshing, state.loading]);

  const refresh = useCallback(() => {
    if (!uid) {
      return;
    }

    setRefreshing(true);
    refreshRequestedRef.current = false;
    if (mode === 'ranked') {
      void refreshMyRankedFeedsWithCooldown({
        feedKind: 'frienzzone',
        force: true,
        uid,
      }).catch(() => undefined);
    }
    setRefreshNonce((currentNonce) => currentNonce + 1);
  }, [mode, uid]);

  return {
    ...state,
    hasMore,
    loadMore,
    loadingMore,
    refresh,
    refreshing,
  };
}
